import { useState } from 'react';

const faqs = [
  {
    q: 'What orthodontic services does Smile By Dr. K offer?',
    a: 'Smile By Dr. K provides comprehensive orthodontic care including traditional braces, InBrace hidden braces, bite correction, teeth straightening, Phase I and Phase II orthodontics, and smile assessments. Both children and adults are welcome at our Chatsworth practice, and virtual consultations are available.',
  },
  {
    q: 'Does Smile By Dr. K offer discreet teeth straightening options?',
    a: 'Yes. InBrace is one of our featured discreet solutions — it sits behind your teeth, completely hidden from view. It\'s an ideal choice for teens and adults who want effective orthodontic results without visible brackets or wires.',
  },
  {
    q: 'When should a child first see an orthodontist?',
    a: 'The American Association of Orthodontists recommends a first orthodontic evaluation by age 7. Early assessment allows Dr. K to identify developing alignment or bite issues and, when needed, begin Phase I treatment to guide jaw growth before all permanent teeth emerge.',
  },
  {
    q: 'Can adults receive orthodontic treatment?',
    a: 'Absolutely. A significant portion of our patients are adults. Whether you\'re correcting a bite issue, addressing shifting teeth, or pursuing treatment you didn\'t receive as a child — it\'s never too late. We offer multiple treatment options designed to fit adult lifestyles.',
  },
];

export default function AISearchSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20 items-start">

          {/* Left — sticky intro */}
          <div className="lg:col-span-2 lg:sticky lg:top-28">
            <div className="flex items-center gap-3 mb-7">
              <span className="w-8 h-px bg-[#C4A96E]" />
              <span
                className="text-[#C4A96E] text-[10px] tracking-[0.32em] uppercase font-semibold"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Common Questions
              </span>
            </div>

            <h2
              className="text-[38px] md:text-[50px] text-[#1A2118] font-semibold leading-[1.08] mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif', letterSpacing: '-0.015em' }}
            >
              What You<br />
              <span className="font-light italic text-[#4C8265]">Need to Know</span>
            </h2>

            <p
              className="text-[#5A7060] text-base leading-[1.85] mb-8"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Honest, clear answers to the questions we hear most from families and new patients considering orthodontic care in Chatsworth.
            </p>

            <div className="bg-[#F0F6F2] rounded-2xl p-6 mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#4C8265]/15 rounded-xl flex-shrink-0">
                  <i className="ri-search-eye-line text-[#4C8265] text-sm" />
                </div>
                <span
                  className="text-[#1A2118] font-semibold text-sm"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  AI &amp; Voice Search Ready
                </span>
              </div>
              <p
                className="text-[#5A7060] text-xs leading-relaxed"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                These answers are structured for AI assistants and voice searches — so you can find us however you search.
              </p>
            </div>

            <a
              href="https://smilebydrkortez.com/faq"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 text-[#1A2118] font-medium text-sm cursor-pointer group"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              <span className="border-b border-[#1A2118]/25 group-hover:border-[#1A2118] transition-colors pb-0.5">
                See All FAQs
              </span>
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" />
            </a>
          </div>

          {/* Right — accordion */}
          <div className="lg:col-span-3 flex flex-col gap-2.5">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`rounded-2xl border transition-all duration-300 overflow-hidden cursor-pointer ${
                  openIndex === idx
                    ? 'bg-[#F7F6F3] border-[#D5E8DA]'
                    : 'bg-white border-[#EAEAEA] hover:border-[#C8D8CC]'
                }`}
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setOpenIndex(openIndex === idx ? null : idx)}
              >
                <div className="flex items-start justify-between gap-4 px-6 py-5">
                  <p
                    className={`font-semibold leading-relaxed transition-colors duration-200 ${
                      openIndex === idx ? 'text-[#1A2118]' : 'text-[#2A3D2E]'
                    }`}
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '19px' }}
                  >
                    {faq.q}
                  </p>
                  <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 mt-0.5 ${
                    openIndex === idx ? 'bg-[#1C3528]' : 'bg-[#F0F6F2]'
                  }`}>
                    <i className={`${openIndex === idx ? 'ri-subtract-line text-white' : 'ri-add-line text-[#4C8265]'} text-sm`} />
                  </div>
                </div>
                {openIndex === idx && (
                  <div className="px-6 pb-6">
                    <div className="h-px bg-[#DFF0E6] mb-4" />
                    <p
                      className="text-[#4A6350] text-sm leading-[1.9]"
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
